import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { apiFetch } from '../../lib/api';
import { messageForFailedResponse } from '../../lib/formatApiError';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { ProductIdentity } from '@/components/products';
import { defaultLoadedQuantities } from '../../lib/refillSession';
import { machineDetailPath } from '../../lib/entityPaths';

type RefillSessionStatus = 'PICKING' | 'IN_TRANSIT' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';

interface RefillLine {
  id: string;
  machineSlotId: string;
  slotNumber: number;
  slotLabel: string | null;
  productId: string | null;
  productName: string | null;
  productSku: string | null;
  productImageUrl: string | null;
  quantityBefore: number;
  quantityExpected: number;
  quantityLoaded: number | null;
  quantityAfter: number | null;
}

interface RefillVariance {
  productId: string;
  productName: string | null;
  productSku: string | null;
  quantityPicked: number;
  quantityLoaded: number;
  leftover: number;
}

interface RefillSessionData {
  id: string;
  machineId: string;
  machineName: string;
  venueName?: string;
  status: RefillSessionStatus;
  lines: RefillLine[];
  variance: RefillVariance[];
  leftoversReturned: boolean;
  createdAt: string;
  completedAt: string | null;
  cancelledAt: string | null;
  cancellationReason: string | null;
  notes: string | null;
}

export function RefillSession() {
  const { id: sessionId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [session, setSession] = useState<RefillSessionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadedQuantities, setLoadedQuantities] = useState<Record<string, number>>({});
  const [notes, setNotes] = useState('');

  const fetchSession = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    setError(null);

    try {
      const response = await apiFetch(`/api/v1/refill-sessions/${sessionId}`);

      if (!response.ok) {
        if (response.status === 404) {
          setError('Refill session not found');
        } else {
          throw new Error(await messageForFailedResponse(response, 'Failed to fetch refill session'));
        }
        return;
      }

      const data: RefillSessionData = await response.json();
      setSession(data);

      setLoadedQuantities(defaultLoadedQuantities(data));
      setNotes(data.notes || '');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load refill session');
      console.error('Error fetching refill session:', err);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  const handleQuantityChange = (slotId: string, value: string) => {
    const num = parseInt(value) || 0;
    setLoadedQuantities((prev) => ({ ...prev, [slotId]: Math.max(0, num) }));
  };

  const handleStartRefill = async () => {
    if (!sessionId) return;
    setSaving(true);
    setError(null);

    try {
      const response = await apiFetch(`/api/v1/refill-sessions/${sessionId}/start`, {
        method: 'PATCH',
      });

      if (!response.ok) {
        throw new Error(await messageForFailedResponse(response, 'Failed to start refill'));
      }

      await fetchSession();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start refill');
    } finally {
      setSaving(false);
    }
  };

  const handleCompleteRefill = async () => {
    if (!sessionId || !session) return;
    setSaving(true);
    setError(null);

    try {
      const lines = session.lines.map((line) => ({
        machineSlotId: line.machineSlotId,
        quantityLoaded: loadedQuantities[line.machineSlotId] ?? 0,
      }));

      const response = await apiFetch(`/api/v1/refill-sessions/${sessionId}/complete`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lines, notes }),
      });

      if (!response.ok) {
        throw new Error(await messageForFailedResponse(response, 'Failed to complete refill'));
      }

      // Stay on the page so picked-vs-loaded variance is visible.
      await fetchSession();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to complete refill');
    } finally {
      setSaving(false);
    }
  };

  const handleReturnLeftovers = async () => {
    if (!sessionId) return;
    setSaving(true);
    setError(null);

    try {
      const response = await apiFetch(`/api/v1/refill-sessions/${sessionId}/return-leftovers`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error(await messageForFailedResponse(response, 'Failed to return leftovers'));
      }

      await fetchSession();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to return leftovers');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelRefill = async () => {
    if (!sessionId) return;
    if (!window.confirm('Are you sure you want to cancel this refill session?')) return;

    setSaving(true);
    setError(null);

    try {
      const response = await apiFetch(`/api/v1/refill-sessions/${sessionId}/cancel`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'User cancelled' }),
      });

      if (!response.ok) {
        throw new Error(await messageForFailedResponse(response, 'Failed to cancel refill'));
      }

      if (!session?.machineId) {
        navigate('/refills');
        return;
      }
      navigate(machineDetailPath(session.machineId, 'refills'));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel refill');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="space-y-6">
          <div className="text-center py-8 text-muted-foreground">Loading...</div>
        </div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="min-h-screen bg-background">
        <div className="space-y-6">
          <div className="text-center py-8">
            <p className="text-red-500 mb-4">{error || 'Session not found'}</p>
            <Button onClick={() => navigate('/refills')}>Back to Refills</Button>
          </div>
        </div>
      </div>
    );
  }

  const isEditable = session.status === 'IN_PROGRESS';
  const canStart = session.status === 'PICKING' || session.status === 'IN_TRANSIT';
  const canComplete = session.status === 'IN_PROGRESS';
  const canCancel = session.status !== 'COMPLETED' && session.status !== 'CANCELLED';
  const variance = session.variance ?? [];
  const totalLeftover = variance.reduce((sum, v) => sum + v.leftover, 0);
  const showVariance = session.status === 'COMPLETED' && variance.length > 0;

  // One group per product so picked/leftover (product-level) can span its slot rows.
  const varianceByProduct = new Map(variance.map((v) => [v.productId, v]));
  const productGroups: Array<{ key: string; lines: RefillLine[]; variance?: RefillVariance }> = [];
  for (const line of session.lines) {
    const key = line.productId ?? `slot-${line.machineSlotId}`;
    const group = productGroups.find((g) => g.key === key);
    if (group) {
      group.lines.push(line);
    } else {
      productGroups.push({
        key,
        lines: [line],
        variance: line.productId ? varianceByProduct.get(line.productId) : undefined,
      });
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="space-y-6">
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
          <Link to="/refills" className="hover:underline">Refills</Link>
          <span>/</span>
          <Link to={machineDetailPath(session.machineId, 'refills')} className="hover:underline">
            {session.machineName}
          </Link>
          <span>/</span>
          <span>Refill Session</span>
        </div>

        <h1 className="mb-6 text-3xl font-bold">Refill Session</h1>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {session.status === 'CANCELLED' && (
          <div className="mb-6 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            <p className="font-medium">This session was cancelled</p>
            {session.cancellationReason && <p className="mt-1">Reason: {session.cancellationReason}</p>}
            {session.notes && <p className="mt-1">{session.notes}</p>}
          </div>
        )}

        {session.status === 'COMPLETED' && session.notes && (
          <div className="mb-6 rounded border bg-muted/50 px-4 py-3 text-sm text-muted-foreground">
            {session.notes}
          </div>
        )}

        {session.status === 'IN_TRANSIT' && (
          <div className="mb-6 rounded border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
            Stock is in the van. This session completes when Express updates the machine, or use
            Fill manually.
          </div>
        )}

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Slot Loading</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>Slot</TableHead>
                  <TableHead className="hidden text-right sm:table-cell">Before</TableHead>
                  <TableHead className="text-right">Expected</TableHead>
                  <TableHead className="text-right">Loaded</TableHead>
                  <TableHead className="text-right">After</TableHead>
                  {showVariance && (
                    <>
                      <TableHead className="text-right">Picked</TableHead>
                      <TableHead className="text-right">Leftover</TableHead>
                    </>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {productGroups.map((group) => {
                  const leftover = group.variance?.leftover ?? 0;
                  const rowClass = showVariance && leftover > 0 ? 'bg-amber-50' : undefined;
                  return group.lines.map((line, idx) => (
                    <TableRow key={line.id} className={rowClass}>
                      {idx === 0 && (
                        <TableCell rowSpan={group.lines.length} className="align-top">
                          <ProductIdentity
                            productId={line.productId ?? undefined}
                            name={line.productName ?? line.productSku ?? '—'}
                            sku={line.productSku}
                            imageUrl={line.productImageUrl}
                            size="sm"
                          />
                        </TableCell>
                      )}
                      <TableCell>{line.slotLabel || `#${line.slotNumber}`}</TableCell>
                      <TableCell className="hidden text-right sm:table-cell">{line.quantityBefore}</TableCell>
                      <TableCell className="text-right">{line.quantityExpected}</TableCell>
                      <TableCell className="text-right">
                        {isEditable ? (
                          <Input
                            type="number"
                            min={0}
                            value={loadedQuantities[line.machineSlotId] ?? 0}
                            onChange={(e) => handleQuantityChange(line.machineSlotId, e.target.value)}
                            className="w-20 text-right"
                          />
                        ) : (
                          line.quantityLoaded ?? '-'
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {line.quantityAfter ?? (
                          isEditable
                            ? line.quantityBefore + (loadedQuantities[line.machineSlotId] ?? 0)
                            : '-'
                        )}
                      </TableCell>
                      {showVariance && idx === 0 && (
                        <>
                          <TableCell rowSpan={group.lines.length} className="align-top text-right">
                            {group.variance ? group.variance.quantityPicked : '—'}
                          </TableCell>
                          <TableCell
                            rowSpan={group.lines.length}
                            className={`align-top text-right ${leftover > 0 ? 'font-semibold text-amber-700' : ''}`}
                          >
                            {group.variance ? leftover : '—'}
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ));
                })}
              </TableBody>
            </Table>
            {session.lines.length === 0 && (
              <p className="py-6 text-center text-sm text-muted-foreground">
                {session.notes?.includes('Completed via BrightBlue stock update')
                  ? 'This refill completed in Express. Cloud did not record slot-level leftover for this session.'
                  : 'No slot lines on this session.'}
              </p>
            )}
            {showVariance && totalLeftover > 0 && (
              <div className="mt-4 flex flex-wrap items-center justify-between gap-2 rounded border border-amber-200 bg-amber-50 px-4 py-3">
                <p className="text-sm text-amber-800">
                  {session.leftoversReturned
                    ? 'Leftovers have been returned to the warehouse.'
                    : `${totalLeftover} unit${totalLeftover === 1 ? '' : 's'} picked but not loaded into the machine.`}
                </p>
                {!session.leftoversReturned && (
                  <Button size="sm" onClick={handleReturnLeftovers} disabled={saving}>
                    Return leftovers to warehouse
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {isEditable && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <textarea
                className="w-full min-h-[100px] p-3 border rounded-md"
                placeholder="Any issues during refill..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </CardContent>
          </Card>
        )}

        <div className="flex flex-wrap justify-between gap-2">
          <div>
            {canCancel && (
              <Button variant="outline" onClick={handleCancelRefill} disabled={saving}>
                Cancel Session
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            {canStart && (
              <Button
                variant={session.status === 'IN_TRANSIT' ? 'outline' : 'default'}
                onClick={handleStartRefill}
                disabled={saving}
              >
                {session.status === 'IN_TRANSIT' ? 'Fill manually' : 'Start Refill at Machine'}
              </Button>
            )}
            {canComplete && (
              <Button onClick={handleCompleteRefill} disabled={saving}>
                Complete Refill
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default RefillSession;
